import { CaseState } from '@/constants/caseStates';
import { WorkflowType } from '@/constants/dispositions';
import { useCaseRecordCalls } from '@/hooks/useCalls';
import { useCase } from '@/hooks/useCases';
import { useCaseRecordEligibilityChecks } from '@/hooks/useEligibilityChecks';
import { useStore } from '@/store/index';
import { isHumanAuditState, isHumanCallState } from '@/utils/caseStateHelpers';

/**
 * Check if case state should show Summary by default
 */
const shouldShowSummaryForState = (state: string | undefined): boolean => {
  if (!state) return false;

  const summaryStates: ReadonlySet<string> = new Set([
    CaseState.COMPLETED,
    CaseState.INPUT_AUDIT_NEEDED,
    CaseState.HUMAN_STEP_MISSING_INFO,
  ]);

  return summaryStates.has(state) || isHumanCallState(state) || isHumanAuditState(state);
};

/**
 * Hook to determine if the Summary tab should be shown by default
 *
 * This hook consolidates all the logic for determining when to show
 * the Summary tab instead of the Review tab in CaseDetailsModal.
 *
 * Logic priority:
 * 1. PQC workflow type → Always show Summary
 * 2. Specific case states (completed, audit, human intervention) → Show Summary
 * 3. No data available (no calls AND no eligibility checks) → Show Summary
 * 4. Otherwise → Show Review (returns false)
 *
 * @param caseId - The case record ID (string | null to match CaseDetailsModal props)
 * @returns true if Summary tab should be shown by default, false for Review tab
 */
export const useShouldShowSummaryByDefault = (caseId: string | null): boolean => {
  const { getSelectedWorkflow } = useStore();
  const selectedWorkflow = getSelectedWorkflow();
  const { data: caseDetails } = useCase(caseId);
  const { data: callsData } = useCaseRecordCalls(caseId);
  const { data: eligibilityChecksData } = useCaseRecordEligibilityChecks(caseId);

  const isPQCWorkflow = selectedWorkflow?.workflow_type === WorkflowType.PQC;
  if (isPQCWorkflow) return true;

  if (shouldShowSummaryForState(caseDetails?.state)) return true;
  const hasNoCalls = !callsData?.calls?.length;
  const hasNoEligibilityChecks = !eligibilityChecksData?.eligibility_checks?.length;

  return hasNoCalls && hasNoEligibilityChecks;
};
